import { ListOrdered, Shield, TrendingDown, TrendingUp } from 'lucide-react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { SectorRRGPoint } from '../types';
import { cn, formatNum, formatPct, quadrantTheme } from '../utils/formatters';

interface Props {
    data: SectorRRGPoint[] | undefined;
}

// Returns the emerald/rose class for a signed return cell.
const returnColor = (v: number): string =>
    v > 0
        ? 'text-emerald-600 dark:text-emerald-400'
        : v < 0
          ? 'text-rose-600 dark:text-rose-400'
          : 'text-slate-500 dark:text-slate-400';

export const SectorRankingsTable: React.FC<Props> = ({ data }) => {
    // Defensive: always render leaders-first even if the caller forgot to sort.
    const rows = [...(data ?? [])].sort((a, b) => b.rs_ratio - a.rs_ratio);

    return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm flex flex-col h-full overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 p-5 border-b border-slate-100 dark:border-slate-800">
                <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-lg">
                    <ListOrdered className="w-5 h-5" />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                        Sector Rankings
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Relative-strength leaders, benchmarked to SPY.
                    </p>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto flex-1">
                {rows.length === 0 ? (
                    <div className="flex h-full min-h-[240px] items-center justify-center text-slate-400 text-sm font-medium">
                        No ranking data available.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-800">
                                <th className="px-4 py-3 font-bold">#</th>
                                <th className="px-4 py-3 font-bold">Sector</th>
                                <th className="px-4 py-3 font-bold">Quadrant</th>
                                <th className="px-4 py-3 font-bold text-right">RS-Ratio</th>
                                <th className="px-4 py-3 font-bold text-right">RS-Mom</th>
                                <th className="px-4 py-3 font-bold text-right">1M</th>
                                <th className="px-4 py-3 font-bold text-right">3M</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => {
                                const theme = quadrantTheme(r.rrg_quadrant);
                                return (
                                    <tr
                                        key={r.ticker}
                                        className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
                                    >
                                        <td className="px-4 py-3 font-mono font-bold text-slate-400 dark:text-slate-500">
                                            {i + 1}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <Link
                                                    to={`/dashboard/etf-info-page?symbol=${encodeURIComponent(r.ticker)}`}
                                                    title={`Open ETF deep dive for ${r.ticker}`}
                                                    className="font-bold text-slate-900 dark:text-slate-100 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline underline-offset-2 transition-colors"
                                                >
                                                    {r.ticker}
                                                </Link>
                                                {r.defensive && (
                                                    <span
                                                        title="Defensive sector"
                                                        className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"
                                                    >
                                                        <Shield className="w-3 h-3" />
                                                        Def
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-slate-500 dark:text-slate-400">
                                                {r.name}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={cn(
                                                    'text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border',
                                                    theme.badge,
                                                )}
                                            >
                                                {r.rrg_quadrant}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono font-bold text-slate-700 dark:text-slate-200">
                                            {formatNum(r.rs_ratio)}
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono text-slate-600 dark:text-slate-300">
                                            {formatNum(r.rs_momentum)}
                                        </td>
                                        <td
                                            className={cn(
                                                'px-4 py-3 text-right font-mono font-semibold',
                                                returnColor(r.momentum_1m),
                                            )}
                                        >
                                            <span className="inline-flex items-center gap-0.5 justify-end">
                                                {r.momentum_1m > 0 ? (
                                                    <TrendingUp className="w-3 h-3" />
                                                ) : r.momentum_1m < 0 ? (
                                                    <TrendingDown className="w-3 h-3" />
                                                ) : null}
                                                {formatPct(r.momentum_1m)}
                                            </span>
                                        </td>
                                        <td
                                            className={cn(
                                                'px-4 py-3 text-right font-mono font-semibold',
                                                returnColor(r.momentum_3m),
                                            )}
                                        >
                                            {formatPct(r.momentum_3m)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default SectorRankingsTable;
